import { useMutation } from '@apollo/client/react';
import {
  Flexbox,
  Sentiments,
  Sizes,
  Text,
  Variants,
} from '@sede-enterprise/shell-ds-react-framework';
import { getNextValidAvailabilitySp } from '@see-power-pft-common-js/availability/utils';
import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { ShellModal } from 'components/Modal/ShellModal';
import { usePlutoAvailabilityApiQuery } from 'hooks/useQuery/plutoAvailabilityApi';
import {
  PRODUCE_ASSET_AVAILABILITIES_UPDATE,
  PRODUCE_SITE_AVAILABILITIES_UPDATE,
} from 'pages/Availability/mutations';
import { areEqualUpdatableContentProperties } from 'pages/Availability/no-changes';
import {
  GET_ASSET_AVAILABILITY,
  GET_SITE_AVAILABILITY,
} from 'pages/Availability/queries';
import {
  AssetAvailability,
  ProduceAssetAvailabilityUpdateInputItem,
  ProduceSiteAvailabilityUpdateItemDto,
  SiteAvailability,
} from 'pages/Availability/types';
import { useStore } from 'store';
import { TableActionType } from 'store/availability';
import { ApiName } from 'types/apiName';

interface CancelAndEndModalModalProps {
  onClose: (type: TableActionType) => void;
  availabilityId: number;
  type: 'cancel' | 'end' | 'endAndStartNew';
  availabilityControl: SiteAvailabilityControl;
}

const getReadableType = (type: TableActionType): string =>
  type ? type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase() : '';

export const CancelAndEndModal: React.FC<CancelAndEndModalModalProps> = ({
  onClose,
  availabilityId,
  availabilityControl,
  type,
}) => {
  const { setAlert } = useStore((state) => state);
  const isAssetLevel =
    availabilityControl === SiteAvailabilityControl.AssetLevel;

  // Site availability query
  const {
    data: siteEntryData,
    loading: siteEntryLoading,
    error: siteEntryError,
  } = usePlutoAvailabilityApiQuery<{
    siteAvailability: SiteAvailability & {
      createdByUserEmail: string;
    };
  }>(GET_SITE_AVAILABILITY, {
    skip: availabilityId === null || isAssetLevel,
    variables: {
      id: availabilityId,
    },
  });

  // Asset availability query
  const {
    data: assetEntryData,
    loading: assetEntryLoading,
    error: assetEntryError,
  } = usePlutoAvailabilityApiQuery<{
    assetAvailability: AssetAvailability & {
      createdByUserEmail: string;
    };
  }>(GET_ASSET_AVAILABILITY, {
    skip: availabilityId === null || !isAssetLevel,
    variables: {
      id: availabilityId,
    },
  });

  const currentEntryLoading = isAssetLevel
    ? assetEntryLoading
    : siteEntryLoading;
  const currentEntryError = isAssetLevel ? assetEntryError : siteEntryError;

  const [mutateProduceSiteAvailabilities] =
    useMutation<ProduceSiteAvailabilityUpdateItemDto>(
      PRODUCE_SITE_AVAILABILITIES_UPDATE,
      {
        context: {
          apiName: ApiName.plutoAvailabilityApi,
        },
      },
    );

  const [mutateProduceAssetAvailabilities] =
    useMutation<ProduceAssetAvailabilityUpdateInputItem>(
      PRODUCE_ASSET_AVAILABILITIES_UPDATE,
      {
        context: {
          apiName: ApiName.plutoAvailabilityApi,
        },
      },
    );

  const parseSiteCancellationData = (
    siteAvailability: SiteAvailability,
  ): ProduceSiteAvailabilityUpdateItemDto => ({
    sourceName: siteAvailability.sourceName,
    sourceId: siteAvailability.sourceId,
    isCancelled: true,
    start: new Date(siteAvailability.start),
    end: siteAvailability.end ? new Date(siteAvailability.end) : undefined,
    notes: siteAvailability.notes ?? undefined,
    lastUpdatedByUserAt: new Date(siteAvailability.updatedByUserAt!),
  });

  const parseSiteEndingData = (
    siteAvailability: SiteAvailability,
  ): ProduceSiteAvailabilityUpdateItemDto => {
    return {
      sourceName: siteAvailability.sourceName,
      sourceId: siteAvailability.sourceId,
      start: new Date(siteAvailability.start),
      end: getNextValidAvailabilitySp(new Date()),
      notes: siteAvailability.notes ?? undefined,
      isCancelled: false,
      lastUpdatedByUserAt: new Date(siteAvailability.updatedByUserAt!),
    };
  };

  const parseAssetCancellationData = (
    assetAvailability: AssetAvailability,
  ): ProduceAssetAvailabilityUpdateInputItem => ({
    sourceName: assetAvailability.sourceName,
    sourceId: assetAvailability.sourceId,
    isCancelled: true,
    start: new Date(assetAvailability.start),
    end: assetAvailability.end ? new Date(assetAvailability.end) : undefined,
    notes: assetAvailability.notes ?? undefined,
    lastUpdatedByUserAt: new Date(assetAvailability.updatedByUserAt!),
  });

  const parseAssetEndingData = (
    assetAvailability: AssetAvailability,
  ): ProduceAssetAvailabilityUpdateInputItem => {
    return {
      sourceName: assetAvailability.sourceName,
      sourceId: assetAvailability.sourceId,
      start: new Date(assetAvailability.start),
      end: getNextValidAvailabilitySp(new Date()),
      notes: assetAvailability.notes ?? undefined,
      isCancelled: false,
      lastUpdatedByUserAt: new Date(assetAvailability.updatedByUserAt!),
    };
  };

  const handleAction = async () => {
    try {
      if (isAssetLevel) {
        const assetAvailability = assetEntryData?.assetAvailability;
        if (!assetAvailability || currentEntryLoading || currentEntryError) {
          return;
        }

        const item: ProduceAssetAvailabilityUpdateInputItem =
          type === 'cancel'
            ? parseAssetCancellationData(assetAvailability)
            : parseAssetEndingData(assetAvailability);

        await mutateProduceAssetAvailabilities({
          variables: {
            input: {
              items: [item],
            },
          },
        });
      } else {
        const siteAvailability = siteEntryData?.siteAvailability;
        if (!siteAvailability || currentEntryLoading || currentEntryError) {
          return;
        }

        const item: ProduceSiteAvailabilityUpdateItemDto =
          type === 'cancel'
            ? parseSiteCancellationData(siteAvailability)
            : parseSiteEndingData(siteAvailability);

        await mutateProduceSiteAvailabilities({
          variables: {
            input: {
              items: (() => {
                const isNoChange = areEqualUpdatableContentProperties(
                  siteAvailability,
                  item,
                );

                return !isNoChange ? [item] : [];
              })(),
            },
          },
        });
      }

      onClose(type);
      setAlert(
        'success',
        `Successfully ${type === 'cancel' ? 'cancelled' : 'ended'} reduction with ID ${availabilityId}`,
      );
    } catch (err) {
      onClose(null);
      setAlert(
        'error',
        `Error ${type === 'cancel' ? ' cancelling' : ' ending'} reduction with ID ${availabilityId}. Please try again later.`,
      );
    }
  };

  return (
    <ShellModal
      open={true}
      onClose={() => onClose(null)}
      loading={currentEntryLoading}
      title="Are you sure?"
      actions={[
        {
          label: 'Keep',
          action: () => onClose(null),
          props: {
            variant: Variants.Outlined,
            sentiment: Sentiments.Primary,
          },
        },
        {
          label: `Yes, ${getReadableType(type)}`,
          action: handleAction,
          props: {
            variant: Variants.Filled,
            sentiment: Sentiments.Negative,
          },
        },
      ]}
      showFooter={true}
      size={Sizes.Small}
      zIndex={100}
      minWidth={400}
    >
      <Flexbox flexDirection={'column'} style={{ padding: 12 }} gap="4px">
        <Text type={'p'} size={Sizes.Small}>
          Please confirm you are
          {type === 'cancel' ? ' cancelling ' : ' ending '}a reported reduction
          with id {availabilityId}?
        </Text>
        {type === 'end' && (
          <Text type={'p'} size={Sizes.Small}>
            This will end the reduction at the next settlement period.
          </Text>
        )}
      </Flexbox>
    </ShellModal>
  );
};
